import { Box, Text } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './components/Button';
import { CenteredScreen } from './screens';
import { logError } from './logger';

interface Props {
  /** Plugin name, used as the logging context. */
  app: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so the plugin shows a recoverable message instead
 * of a blank iframe, and reports them through the shared {@link logError}.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError(error, {
      app: this.props.app,
      phase: 'render',
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <CenteredScreen>
          <Box color="figma.error">
            <AlertTriangle size={28} />
          </Box>
          <Text fontSize="md" fontWeight="600" color="figma.text">
            Something went wrong
          </Text>
          <Text fontSize="sm" color="figma.textSecondary" maxW="320px">
            {this.state.error.message}
          </Text>
          <Button colorPalette="accent" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
        </CenteredScreen>
      );
    }
    return this.props.children;
  }
}
